import React from 'react';
import { FiGlobe, FiTrendingUp,  FiUsers, FiBarChart2,  FiSearch, FiMonitor } from 'react-icons/fi';

const SEOSchoolWebsite = () => {
  const seoBenefits = [
    {
      icon: <FiSearch className="w-6 h-6" />,
      title: 'Parents Find You First on Google',
      description: 'When parents search for "best schools near me" or "top colleges in [city]", your website should appear at the top of search results. An SEO-optimized website ensures you are visible when parents are searching for educational options for their children.',
      stat: '89% of parents use Google to research schools',
      color: 'bg-indigo-50'
    },
    {
      icon: <FiGlobe className="w-6 h-6" />,
      title: 'Your Content, Your Rules',
      description: 'Facebook controls your content. They can limit reach, change algorithms, or even shut down your page. With a website, you own your content. You decide what to share and how to share it.',
      stat: '60% of institutions have had Facebook reach decreased by algorithm changes',
      color: 'bg-emerald-50'
    },
    {
      icon: <FiUsers className="w-6 h-6" />,
      title: 'Build Trust and Credibility',
      description: 'A professional website signals legitimacy and quality. Parents trust institutions with a well-designed website. It shows you are serious about your institution\'s brand and reputation.',
      stat: '75% of parents judge an institution\'s credibility by its website',
      color: 'bg-amber-50'
    },
    {
      icon: <FiMonitor className="w-6 h-6" />,
      title: 'Complete Control Over User Experience',
      description: 'On Facebook, you are limited to their design and layout. On your website, you control everything—colors, navigation, content, and user journey. You can guide parents through the information they need.',
      stat: '85% of institutions want more control over their online presence',
      color: 'bg-rose-50'
    },
    {
      icon: <FiBarChart2 className="w-6 h-6" />,
      title: 'Showcase Your Programs and Achievements',
      description: 'Your website can feature detailed information about programs, faculty, facilities, achievements, and student success stories. Facebook is too limited for comprehensive information.',
      stat: '90% of institutions have more content than Facebook allows',
      color: 'bg-purple-50'
    },
    {
      icon: <FiTrendingUp className="w-6 h-6" />,
      title: 'Long-Term Asset',
      description: 'Facebook pages are temporary. They can be deleted, restricted, or lose reach. Your website is a permanent asset that grows in value over time. Every piece of content builds your digital presence.',
      stat: 'Website content remains valuable for 2+ years',
      color: 'bg-blue-50'
    }
  ];

  const facebookLimitations = [
    {
      title: 'Limited Organic Reach',
      description: 'Facebook shows your posts to only 5-10% of your followers unless you pay. Your content is not reaching most of your audience.'
    },
    {
      title: 'Algorithm Dependence',
      description: 'You are at the mercy of Facebook\'s algorithm changes. Your reach can drop overnight without warning.'
    },
    {
      title: 'No SEO Benefits',
      description: 'Facebook pages do not rank well on Google. Parents searching for your institution may not find your Facebook page.'
    },
    {
      title: 'Limited Design Options',
      description: 'You cannot customize the look and feel of your Facebook page beyond basic settings. Your brand is limited.'
    },
    {
      title: 'Data Ownership',
      description: 'Facebook owns your page data. If they shut down your page, you lose everything—content, followers, and history.'
    },
    {
      title: 'No Analytics Control',
      description: 'Facebook provides limited analytics. You cannot track user behavior or measure engagement effectively.'
    }
  ];

  return (
    <>
      {/* Introduction */}
      <p className="text-lg text-slate-600 leading-relaxed mb-6">
        Many educational institutions in India rely heavily on Facebook for their online presence. They post updates, share photos, and communicate with parents. While Facebook is useful for engagement, it is not a substitute for a professional, SEO-optimized website.
      </p>
      
      <p className="text-lg text-slate-600 leading-relaxed mb-6">
        <strong className="text-slate-900">Your Facebook page is not your digital identity.</strong> It is a rented space on a platform you don't control. Your website is your digital home—where you control the content, the experience, and the message.
      </p>

      <p className="text-lg text-slate-600 leading-relaxed mb-8">
        Here's why your institution needs an SEO-optimized website—and why Facebook alone is not enough.
      </p>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        <div className="bg-white rounded-xl p-6 text-center border">
          <div className="text-3xl font-extrabold text-indigo-600">89%</div>
          <div className="text-sm text-slate-500">Parents use Google to research schools</div>
        </div>
        <div className="bg-white rounded-xl p-6 text-center border">
          <div className="text-3xl font-extrabold text-emerald-600">75%</div>
          <div className="text-sm text-slate-500">Judge credibility by website</div>
        </div>
        <div className="bg-white rounded-xl p-6 text-center border">
          <div className="text-3xl font-extrabold text-amber-600">2+</div>
          <div className="text-sm text-slate-500">Years of website content value</div>
        </div>
      </div>

      <h2 className="text-2xl font-bold text-slate-900 mb-6">Why SEO Matters for Educational Institutions</h2>

      {/* SEO Benefits */}
      {seoBenefits.map((item, i) => (
        <div key={i} className={`${item.color} rounded-2xl p-6 mb-6 border`}>
          <div className="flex items-start gap-4">
            <div className="bg-white rounded-xl p-3 shadow-sm flex-shrink-0 mt-1">
              {item.icon}
            </div>
            <div className="flex-1">
              <h3 className="text-xl font-bold text-slate-900 mb-2">{item.title}</h3>
              <p className="text-slate-600 leading-relaxed text-sm mb-4">{item.description}</p>
              <div className="bg-white/60 rounded-xl p-3 border border-amber-200">
                <p className="text-sm font-semibold text-amber-600">📊 {item.stat}</p>
              </div>
            </div>
          </div>
        </div>
      ))}

      {/* Facebook Limitations */}
      <div className="mt-8 bg-white rounded-2xl border border-slate-200 p-6 sm:p-8">
        <h3 className="text-xl font-bold text-slate-900 mb-4">Why Facebook Alone is Not Enough</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {facebookLimitations.map((item, i) => (
            <div key={i} className="bg-red-50 rounded-xl p-4 border border-red-200">
              <div className="flex items-start gap-2">
                <span className="text-red-500 font-bold text-sm">⚠️</span>
                <div>
                  <h4 className="font-bold text-slate-900 text-sm">{item.title}</h4>
                  <p className="text-xs text-slate-600 mt-1">{item.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Comparison Section */}
      <div className="mt-8 bg-white rounded-2xl border border-slate-200 p-6 sm:p-8">
        <h3 className="text-xl font-bold text-slate-900 mb-4">Facebook Page vs. SEO-Optimized Website</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="bg-red-50 rounded-xl p-4 border border-red-200">
            <div className="text-sm font-semibold text-red-700 mb-2">Facebook Page Only</div>
            <ul className="text-sm text-slate-600 space-y-2">
              <li className="flex items-start gap-2">• <span>Limited organic reach</span></li>
              <li className="flex items-start gap-2">• <span>Algorithm dependent</span></li>
              <li className="flex items-start gap-2">• <span>No Google ranking</span></li>
              <li className="flex items-start gap-2">• <span>Limited design</span></li>
              <li className="flex items-start gap-2">• <span>Facebook owns your data</span></li>
              <li className="flex items-start gap-2">• <span>Restricted analytics</span></li>
            </ul>
          </div>
          <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-200">
            <div className="text-sm font-semibold text-emerald-700 mb-2">SEO-Optimized Website</div>
            <ul className="text-sm text-slate-600 space-y-2">
              <li className="flex items-start gap-2">• <span>Rank #1 on Google</span></li>
              <li className="flex items-start gap-2">• <span>Your content, your rules</span></li>
              <li className="flex items-start gap-2">• <span>High Google ranking</span></li>
              <li className="flex items-start gap-2">• <span>Complete design control</span></li>
              <li className="flex items-start gap-2">• <span>You own your data</span></li>
              <li className="flex items-start gap-2">• <span>Full analytics control</span></li>
            </ul>
          </div>
        </div>
      </div>

      {/* The Parent Journey */}
      <div className="mt-8 bg-amber-50 rounded-2xl p-6 border border-amber-200">
        <h3 className="text-lg font-bold text-amber-900 mb-3">The Parent Journey</h3>
        <p className="text-amber-800 leading-relaxed mb-3">
          When parents begin their search for a school or college, they start with Google. They search for "best schools near me" or "top colleges in [city]." If your institution does not appear in search results, you are invisible to these parents.
        </p>
        <p className="text-amber-800 leading-relaxed mb-3">
          A Facebook page cannot help you here. Facebook does not rank well on Google. Parents will not find your Facebook page in search results. They will find websites that are SEO-optimized.
        </p>
        <p className="text-amber-800 leading-relaxed">
          <strong>If you are not on Google's first page, you are not in the conversation.</strong>
        </p>
      </div>

      {/* Why Truyon Section */}
      <div className="mt-8 bg-indigo-50 rounded-2xl p-6 border border-indigo-200">
        <h3 className="text-lg font-bold text-indigo-900 mb-3">Why Truyon is the Best Choice for SEO-Optimized Websites</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">🔍</div>
            <h4 className="font-bold text-slate-900 text-sm">SEO Optimized</h4>
            <p className="text-xs text-slate-600">Built-in SEO for better Google ranking</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">📱</div>
            <h4 className="font-bold text-slate-900 text-sm">Mobile Responsive</h4>
            <p className="text-xs text-slate-600">Works perfectly on all devices</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">⚡</div>
            <h4 className="font-bold text-slate-900 text-sm">Fast Loading</h4>
            <p className="text-xs text-slate-600">Optimized for speed and performance</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">📊</div>
            <h4 className="font-bold text-slate-900 text-sm">Analytics</h4>
            <p className="text-xs text-slate-600">Track visitor behavior and engagement</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">🔗</div>
            <h4 className="font-bold text-slate-900 text-sm">Content Management</h4>
            <p className="text-xs text-slate-600">Easy content updates without coding</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">🌐</div>
            <h4 className="font-bold text-slate-900 text-sm">Integrated ERP</h4>
            <p className="text-xs text-slate-600">ERP + Website in one platform</p>
          </div>
        </div>
      </div>

      {/* Conclusion */}
      <div className="mt-8 bg-indigo-50 rounded-2xl p-6 border border-indigo-200">
        <h3 className="text-lg font-bold text-indigo-900 mb-2">Get Found on Google with Truyon</h3>
        <p className="text-indigo-800 leading-relaxed">
          Your institution deserves to be found. When parents search for educational options, you should appear at the top of Google results. An SEO-optimized website makes this possible.
        </p>
        <p className="text-indigo-800 leading-relaxed mt-3">
          Truyon gives you an SEO-optimized website built for educational institutions. Mobile responsive, fast loading, easy to update, and integrated with your ERP. Get found on Google. Build trust. Attract more students.
        </p>
        <p className="text-indigo-800 leading-relaxed mt-3">
          <strong>Truyon for SEO-optimized websites.</strong> Get found. Get students. Grow your institution.
        </p>
      </div>
    </>
  );
};

export default SEOSchoolWebsite;